import React, { useCallback, useEffect, useState } from 'react';
import { TbReportMedical } from 'react-icons/tb';

import { BLOCKS } from '@contentful/rich-text-types';
import { useLocation } from '@reach/router';
import { Form, Formik } from 'formik';
import { graphql } from 'gatsby';
import { GatsbyImage, StaticImage, getImage } from 'gatsby-plugin-image';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import * as Yup from 'yup';

import RecentArticles from '../components/Blog/RecentArticles';
import DescriptionContent from '../components/DescriptionContent';
import Footer from '../components/Footer';
import Modal from '../components/Modal';
import Seo from '../components/Seo';
import ShareIcon from '../components/ShareIcons/ShareIcon';
import { TextField } from '../components/form/TextFields';
import Header from '../components/navbar/Header';
import MobileNav from '../components/navbar/MobileNav';
import { CtaEntry } from '../components/web/cta';
import { waitlistSignupFromBlog } from '../functions/Klaviyo';
import { useMixpanel } from '../functions/Mixpanel';
import { verifyCaptcha } from '../functions/Recaptcha';
import useScript from '../hooks/useScript';
import LessReference from '../images/blog/referenceMinus.svg';
import MoreReference from '../images/blog/referencePlus.svg';

import * as style from './BlogTemplate.module.css';

const BlogTemplate = (props) => {
  useScript(
    'https://www.google.com/recaptcha/api.js?render=6Lcg_9wiAAAAAMbPViCeVuHvctO7g8wPd3kBUtiO',
  );
  const { mixpanel } = useMixpanel();
  const { data } = props;
  const blogData = data.contentfulBlog;
  const post = blogData.fullBlogPost.childMarkdownRemark;
  const richTextContent = blogData.contentRichText;
  const reference = blogData?.references?.childMarkdownRemark;
  const referenceLength = reference?.htmlAst?.children[0]?.children;
  const titleHeading = blogData.title;
  const image = blogData?.image?.gatsbyImageData;
  const medicalReviewer = blogData?.medicalReviewer?.name;
  const location = useLocation();
  const [emailSent, setEmailSent] = useState(false);
  const [emailNotSent, setEmailNotSent] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const trackEvents = useCallback(() => {
    typeof window !== 'undefined' &&
      window.gtag('event', 'click', {
        category: 'Button Click',
        label: 'Waitlist Conversion',
      });
    mixpanel.track('Join Waitlist Blog', { 'Article Title': titleHeading });
  }, [mixpanel, titleHeading]);

  const shareArticleTrack = useCallback(
    (title, method) => {
      mixpanel.track('Share Article', {
        'Article Title': title,
        'Share Method': method,
      });
    },
    [mixpanel],
  );

  const validate = Yup.object({
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('Email is required'),
  });

  useEffect(() => {
    mixpanel.track('View Article', {
      'Article Title': titleHeading,
      'Popular Article Test': false,
    });
  }, [titleHeading, mixpanel]);

  const [visibleReference, setVisibleReference] = useState(false);

  const viewOrHideReference = () => {
    setVisibleReference(!visibleReference);
  };

  //to check if its browser side before calling window attribute
  const isBrowser = typeof window !== 'undefined';
  const onFinish = (isSuccessful, response) => {
    if (isSuccessful && response.score >= 0.5) {
      setEmailSent(false);
      setEmailNotSent(true);
    } else {
      setEmailNotSent(false);
      setEmailSent(true);
    }
    setModalOpen(true);
  };
  const onClick = (input) => {
    // e.preventDefault();
    isBrowser &&
      window.grecaptcha.ready(function () {
        window.grecaptcha
          .execute('6Lcg_9wiAAAAAMbPViCeVuHvctO7g8wPd3kBUtiO', {
            action: 'submit',
          })
          .then(function (token) {
            verifyCaptcha({ token }, onFinish);
            waitlistSignupFromBlog(input, onFinish);
            trackEvents();
          });
      });
  };

  /* We can use options here from the richText package to capture any block and apply  a class to it. Will have to use the getImage function from gastby and apply gatsby image to auto format assets */
  const options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const { gatsbyImageData } = node.data.target;
        return <GatsbyImage image={getImage(gatsbyImageData)} alt="" />;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const {
          title,
          subheaderText,
          image: ctaImage,
          buttonText,
          link,
          updatedAt,
          ctaDescriptionForTracking,
        } = node.data.target;

        return (
          <CtaEntry
            className="tw-mt-[32px] tw-mb-[32px]"
            title={title}
            body={subheaderText}
            imageData={ctaImage.gatsbyImageData}
            buttonText={buttonText}
            onClick={() => {
              mixpanel.track('BlogCTA Click', {
                Title: title,
                Body: subheaderText,
                ButtonText: buttonText,
                Link: link,
                UpdatedAt: updatedAt,
                CtaDescriptionForTracking: ctaDescriptionForTracking,
              });
            }}
            link={link}
          />
        );
      },
    },
  };

  return (
    <div>
      <Header
        pageName="Blog Content"
        pageCategory="patient"
        link="https://apps.apple.com/app/apple-store/id6446295104?pt=123955580&ct=Web%20Blog&mt=8"
      />
      <MobileNav pageCategory="patient" />
      <Seo
        title={blogData.title}
        description={blogData.summary.summary}
        image={blogData.image && blogData.image.file.url}
        siteUrl={`https://www.alifehealth.com/blog/${blogData.slug}/`}
      />
      <div className={style.allContent}>
        <div className={`blog ${style.blogContainer}`}>
          <div className={style.mainBlogContent}>
            <div aria-hidden="true" className={style.blogBackArrow}>
              <a href="/blog" className={style.blogBack}>
                <p className="subTitle">Alife Blog</p>
              </a>
            </div>
            <h1 className={style.blogTitle}> {blogData.title}</h1>
            <div className={style.contentIconsContainer}>
              <div className={style.blogContent} id="blog-content">
                <div className={style.topIcons}>
                  <ShareIcon
                    url={location.href}
                    description={blogData.title}
                    subject={blogData.title}
                    titleHeading={blogData.title}
                    functionName={shareArticleTrack}
                    position="top"
                  />
                </div>
                <div>
                  <GatsbyImage image={image} alt="" />
                  {medicalReviewer && (
                    <div className={style.medicalReviewerContainer}>
                      <TbReportMedical size="24px" color="#21263A" />
                      <h4 className="h4Emphasis">
                        Medically reviewed by {medicalReviewer}{' '}
                      </h4>
                    </div>
                  )}
                </div>
                <div className={`contentfulLists ${style.blogList}`}>
                  {richTextContent ? (
                    renderRichText(richTextContent, options)
                  ) : (
                    <DescriptionContent {...post} />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={style.IconsContainer}>
            <ShareIcon
              url={location.href}
              description={blogData.title}
              subject={blogData.title}
              titleHeading={blogData.title}
              functionName={shareArticleTrack}
              position="side"
            />
            <div className={style.sideArticlesContainer}>
              <p className={`subTitle ${style.recentHeading}`}>
                Recent Articles
              </p>
              <RecentArticles title={blogData.title} flagType="Recent" />
            </div>
          </div>
        </div>
        <div className={style.referenceContainer}>
          {reference !== undefined && referenceLength.length > 1 && (
            <div className={style.blogReferences}>
              <div className={style.referenceIcon}>
                <p className="subTitle">References</p>
                <img
                  src={!visibleReference ? MoreReference : LessReference}
                  onClick={() => viewOrHideReference()}
                  role="presentation"
                  alt=""
                />
              </div>

              <div
                className={`${style.referenceList} + ${
                  !visibleReference && style.hiddenReference
                }`}
              >
                <DescriptionContent {...reference} />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={style.bottomIconsContainer}>
        <div className={style.bottomShareIcon}>
          <h1>Share this</h1>
          <ShareIcon
            url={location.href}
            description={blogData.title}
            subject={blogData.title}
            titleHeading={blogData.title}
            functionName={shareArticleTrack}
            position="side"
          />
        </div>

        <div className={style.bottomRecentArticlesContainer}>
          <p className="subTitle">Recent Articles</p>
          <RecentArticles title={blogData.title} flagType="Recent" />
        </div>
      </div>

      <div className={style.formContainer}>
        <StaticImage
          src="../images/gradientOrb.png"
          alt="Gradient Orb"
          placeholder="tracedSVG"
          quality="100"
          className={style.gradientOrb}
          objectFit="contain"
        />
        <div className={style.formBody}>
          <h1>Learn everything you need to know about&nbsp;IVF</h1>
          <p>
            Join the newsletter for IVF education, updates on new research, and
            early access to Alife&nbsp;products.
          </p>
          {emailNotSent && modalOpen && (
            <Modal
              setOpenModal={setModalOpen}
              title="There was an error submitting your request, please try again later or use a different email address."
            />
          )}
          {emailSent && modalOpen && (
            <Modal
              setOpenModal={setModalOpen}
              title="Thank you for joining our newsletter!"
              body="Stay informed with IVF education, research updates, and exclusive access to Alife products."
            />
          )}
          <Formik
            initialValues={{
              email: '',
              name: '',
            }}
            validationSchema={validate}
            onSubmit={(values) => {
              const input = {
                ...values,
                patient_or_clinician: 'patient',
              };

              onClick(input);
            }}
          >
            {(formik) => (
              <Form>
                <div className={style.formField}>
                  <TextField
                    placeholder="Email address"
                    name="email"
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                  />
                </div>
                <TextField
                  type="submit"
                  name="submit"
                  value="SIGN UP"
                  className="btn g-recaptcha"
                />
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogTemplate;

export const BlogQuery = graphql`
  query BlogQuery($slug: String!) {
    contentfulBlog(slug: { eq: $slug }) {
      title
      createdAt(formatString: "MMMM D, YYYY")
      medicalReviewer {
        name
        link
      }
      summary {
        summary
        childMarkdownRemark {
          htmlAst
        }
      }
      image {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
        file {
          url
        }
      }
      fullBlogPost {
        childMarkdownRemark {
          htmlAst
        }
      }
      contentRichText {
        raw
        references {
          ... on ContentfulAsset {
            contentful_id
            title
            description
            gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
            __typename
          }
          ... on ContentfulInLineBlogCta {
            contentful_id
            title
            subheaderText
            image {
              gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
            }
            updatedAt(formatString: "MMMM D, YYYY")
            ctaDescriptionForTracking
            link
            buttonText
            __typename
          }
        }
      }
      slug
      references {
        childMarkdownRemark {
          htmlAst
        }
      }
    }
  }
`;
